import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import ReactMarkdown from "react-markdown";
import {
  MessageSquare,
  Plus,
  Send,
  Square,
  Copy,
  RotateCcw,
  Pencil,
  Trash2,
  History,
  Search,
  ArrowUpRight,
  Package,
} from "lucide-react";
import { useApiService } from "../../services/apiService";
import { useAuthenticatedUser } from "../../hooks/useAuth";
import { Button } from "../ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from "../ui/sheet";
import { ToggleGroup, ToggleGroupItem } from "../ui/toggle-group";
import { AddToCollections } from "../product-collections/AddToCollections";
import { AssistantWidget } from "./AssistantWidget";
import {
  conversationPath,
  conversationUrl,
  assistantText,
  productResults,
  widgetSchema,
  type AssistantMessage,
  type Conversation,
  type ConversationSummary,
  type Product,
} from "./assistantTypes";
import "./article-assistant.css";

const starters = [
  {
    title: "Build a breakfast assortment",
    text: "Help me find a breakfast assortment for a foodservice customer. Ask me about their market and preparation facilities.",
  },
  {
    title: "Find the right preparation fit",
    text: "Help me find bakery products that fit my customer's preparation facilities and dietary requirements.",
  },
  {
    title: "Find an alternative product",
    text: "Help me find an alternative to an existing product, keeping my customer's market and supply requirements.",
  },
];

export default function ArticleAssistantChat() {
  const api = useApiService();
  const { user } = useAuthenticatedUser();
  const [items, setItems] = useState<ConversationSummary[]>([]);
  const [active, setActive] = useState<string | null>(() =>
    new URLSearchParams(location.search).get("conversation")
  );
  const [error, setError] = useState("");
  const [working, setWorking] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [initialPrompt, setInitialPrompt] = useState("");
  const [renaming, setRenaming] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [deleting, setDeleting] = useState<string | null>(null);
  const refresh = useCallback(async () => {
    try {
      setItems(await api.get<ConversationSummary[]>(conversationPath));
      setError("");
    } catch {
      setError("Could not load conversations. Please try again.");
    }
  }, [api]);
  useEffect(() => {
    void refresh();
  }, [refresh, user?.sub]);
  useEffect(() => {
    const navigate = () =>
      setActive(new URLSearchParams(location.search).get("conversation"));
    window.addEventListener("popstate", navigate);
    return () => window.removeEventListener("popstate", navigate);
  }, []);
  const choose = (id: string | null) => {
    setHistoryOpen(false);
    setInitialPrompt("");
    setActive(id);
    history.replaceState(
      history.state,
      "",
      id ? conversationUrl(id) : "/?view=article-assistant-chat"
    );
  };
  async function create(prompt = "") {
    setWorking(true);
    try {
      const item = await api.post<ConversationSummary>(conversationPath, {});
      choose(item.id);
      setInitialPrompt(prompt);
      await refresh();
    } catch {
      setError("Could not create a conversation. Please try again.");
    } finally {
      setWorking(false);
    }
  }
  return (
    <div className="article-assistant">
      <header className="assistant-heading">
        <div>
          <h1>Article Assistant</h1>
          <p>From customer brief to the right bakery products.</p>
        </div>
        <div className="assistant-heading-actions">
          <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
            <SheetTrigger asChild>
              <Button variant="outline">
                <History size={16} /> History
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="assistant-history">
              <SheetHeader>
                <SheetTitle>Conversations</SheetTitle>
                <SheetDescription>
                  Your saved customer briefs and product searches.
                </SheetDescription>
              </SheetHeader>
              <aside className="assistant-sidebar" aria-label="Conversations">
                <Button onClick={() => void create()} disabled={working}>
                  <Plus size={16} /> New conversation
                </Button>
                {error && (
                  <div role="alert">
                    {error}
                    <Button variant="outline" onClick={() => void refresh()}>
                      Retry
                    </Button>
                  </div>
                )}
                <nav aria-label="Saved conversations">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className={`assistant-conversation ${
                        active === item.id ? "active" : ""
                      }`}
                    >
                      <button
                        className="assistant-conversation-title"
                        aria-current={active === item.id ? "page" : undefined}
                        onClick={() => choose(item.id)}
                      >
                        <MessageSquare size={15} />
                        <span>{item.title}</span>
                      </button>
                      <div className="assistant-conversation-actions">
                        <button
                          aria-label={`Rename ${item.title}`}
                          onClick={() => {
                            setRenaming(item.id);
                            setTitle(item.title);
                          }}
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          aria-label={`Delete ${item.title}`}
                          onClick={() => setDeleting(item.id)}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                      {renaming === item.id && (
                        <form
                          onSubmit={async (event) => {
                            event.preventDefault();
                            setWorking(true);
                            try {
                              await api.patch(
                                `${conversationPath}/${item.id}`,
                                {
                                  title,
                                }
                              );
                              setRenaming(null);
                              await refresh();
                            } catch {
                              setError(
                                "Could not rename this conversation. Wait for any response to finish and retry."
                              );
                            } finally {
                              setWorking(false);
                            }
                          }}
                        >
                          <input
                            aria-label="Conversation title"
                            maxLength={200}
                            value={title}
                            onChange={(event) => setTitle(event.target.value)}
                            autoFocus
                          />
                          <Button size="sm" disabled={working || !title.trim()}>
                            Save
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => setRenaming(null)}
                          >
                            Cancel
                          </Button>
                        </form>
                      )}
                      {deleting === item.id && (
                        <div role="alert">
                          <p>Delete this conversation and its history?</p>
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={working}
                            onClick={async () => {
                              setWorking(true);
                              try {
                                await api.delete(
                                  `${conversationPath}/${item.id}`
                                );
                                if (active === item.id) choose(null);
                                setDeleting(null);
                                await refresh();
                              } catch {
                                setError(
                                  "Could not delete this conversation. Wait for any response to finish and retry."
                                );
                              } finally {
                                setWorking(false);
                              }
                            }}
                          >
                            Delete
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeleting(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </nav>
              </aside>
            </SheetContent>
          </Sheet>
          <Button onClick={() => void create()} disabled={working}>
            <Plus size={16} /> New conversation
          </Button>
        </div>
      </header>
      {error && !historyOpen && (
        <p role="alert">
          {error}{" "}
          <Button variant="outline" onClick={() => void refresh()}>
            Retry
          </Button>
        </p>
      )}
      <div className="assistant-layout">
        {active ? (
          <ConversationChat
            key={`${user?.sub}:${active}`}
            id={active}
            onChanged={refresh}
            initialPrompt={initialPrompt}
          />
        ) : (
          <main className="assistant-empty">
            <div className="assistant-eyebrow">
              <Search size={16} /> PRODUCT DISCOVERY
            </div>
            <h2>
              Find products for
              <br />
              your customer
            </h2>
            <p>
              Describe what your customer needs. Explore the range, check the
              facts, and build a selection that fits.
            </p>
            <div className="assistant-lenses">
              <span>Commercial fit</span>
              <span>Consumer appeal</span>
              <span>Operator suitability</span>
              <span>Production & supply</span>
            </div>
            <div className="assistant-starters">
              {starters.map((starter) => (
                <Button
                  key={starter.title}
                  variant="outline"
                  disabled={working}
                  onClick={() => void create(starter.text)}
                >
                  {starter.title}
                  <ArrowUpRight size={16} />
                </Button>
              ))}
            </div>
            <Button onClick={() => void create()} disabled={working}>
              Start a conversation
            </Button>
          </main>
        )}
      </div>
    </div>
  );
}

function ConversationChat({
  id,
  onChanged,
  initialPrompt,
}: {
  id: string;
  onChanged: () => Promise<void>;
  initialPrompt: string;
}) {
  const api = useApiService();
  const { getToken } = useAuthenticatedUser();
  const [input, setInput] = useState("");
  const [selected, setSelected] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [serverBusy, setServerBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [mobileView, setMobileView] = useState<"conversation" | "results">(
    "conversation"
  );
  const [openedResult, setOpenedResult] = useState<{
    key: string;
    latest: string;
  } | null>(null);
  const sentInitial = useRef(false);
  const mounted = useRef(true);
  const end = useRef<HTMLDivElement>(null);
  const transport = useMemo(
    () =>
      new DefaultChatTransport<AssistantMessage>({
        api: `${
          import.meta.env.VITE_API_URL || "/api"
        }${conversationPath}/${id}/messages`,
        headers: async () => ({ Authorization: `Bearer ${await getToken()}` }),
        prepareSendMessagesRequest: ({ messages, trigger, body }) => {
          const last = [...messages]
            .reverse()
            .find((message) => message.role === "user");
          return {
            body: {
              messageId: last?.id,
              text: last?.parts
                .filter((part) => part.type === "text")
                .map((part) => part.text)
                .join("\n"),
              selectedProductIds: last?.parts.flatMap((part) =>
                part.type === "data-selection"
                  ? part.data.map((product) => product.entityId)
                  : []
              ),
              retry: body?.retry ?? trigger === "regenerate-message",
            },
          };
        },
      }),
    [id, getToken]
  );
  const {
    messages,
    setMessages,
    sendMessage,
    regenerate,
    status,
    error,
    stop,
    clearError,
  } = useChat<AssistantMessage>({
    id,
    transport,
    throttle: 50,
    generateId: () => crypto.randomUUID(),
    onFinish: ({ isError }) => {
      if (mounted.current) {
        void onChanged();
        if (!isError) void reload();
      }
    },
  });
  const busy = status === "submitted" || status === "streaming";
  const reload = useCallback(async () => {
    if (mounted.current) setLoading(true);
    try {
      const conversation = await api.get<Conversation>(
        `${conversationPath}/${id}`
      );
      if (mounted.current) {
        setMessages(conversation.messages);
        setServerBusy(conversation.generating);
        setLoadError("");
      }
    } catch {
      if (mounted.current)
        setLoadError(
          "Could not load this conversation. It may have been deleted or belong to another user."
        );
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [api, id, setMessages]);
  useEffect(() => {
    mounted.current = true;
    void reload();
    return () => {
      mounted.current = false;
      void stop();
    };
  }, [reload, stop]);
  useEffect(() => {
    if (!serverBusy || busy) return;
    const timer = setInterval(() => void reload(), 2000);
    return () => clearInterval(timer);
  }, [serverBusy, busy, reload]);
  useEffect(() => {
    end.current?.scrollIntoView?.({ block: "nearest" });
  }, [messages]);
  const blocked = loading || !!loadError || busy || serverBusy;
  const results = useMemo(() => productResults(messages), [messages]);
  const latestResult = results.at(-1);
  const visibleResult =
    (openedResult?.latest === latestResult?.key
      ? results.find((result) => result.key === openedResult?.key)
      : undefined) ?? latestResult;
  const resultMessage = messages.find(
    (message) => message.id === visibleResult?.messageId
  );
  const resultWidgets =
    resultMessage?.parts.flatMap((part) => {
      if (part.type !== "data-widget") return [];
      const parsed = widgetSchema.safeParse(part.data);
      return parsed.success &&
        (parsed.data.component === "AttributeHighlights" ||
          parsed.data.component === "SourceReferences")
        ? [parsed.data]
        : [];
    }) ?? [];
  const send = useCallback(
    async (text: string) => {
      if (!text.trim() || blocked) return;
      setInput("");
      setMobileView("conversation");
      setNotice("");
      clearError();
      const products = selected;
      setSelected([]);
      await sendMessage({
        parts: [
          { type: "text", text: text.trim() },
          ...(products.length
            ? [{ type: "data-selection" as const, data: products }]
            : []),
        ],
      });
    },
    [blocked, clearError, selected, sendMessage]
  );
  useEffect(() => {
    if (initialPrompt && !blocked && !messages.length && !sentInitial.current) {
      sentInitial.current = true;
      void send(initialPrompt);
    }
  }, [initialPrompt, blocked, messages.length, send]);
  async function retry() {
    const last = [...messages]
      .reverse()
      .find((message) => message.role === "user");
    if (!last || blocked) return;
    try {
      const saved = await api.get<Conversation>(`${conversationPath}/${id}`);
      if (saved.generating) {
        setServerBusy(true);
        return;
      }
      const existing = saved.messages.findIndex(
        (message) => message.id === last.id
      );
      if (
        existing >= 0 &&
        saved.messages
          .slice(existing + 1)
          .some((message) => message.metadata?.status === "complete")
      ) {
        setMessages(saved.messages);
        clearError();
        return;
      }
      clearError();
      await regenerate({ body: { retry: existing >= 0 } });
    } catch {
      setNotice("Could not retry. Check your connection and try again.");
    }
  }
  const latest = messages.at(-1);
  const canRetry =
    !blocked &&
    messages.some((message) => message.role === "user") &&
    (!!error ||
      latest?.metadata?.status === "incomplete" ||
      latest?.role === "user");
  return (
    <main className="assistant-workspace" data-mobile-view={mobileView}>
      <ToggleGroup
        className="assistant-mobile-tabs"
        type="single"
        value={mobileView}
        onValueChange={(value) => {
          if (value === "conversation" || value === "results")
            setMobileView(value);
        }}
        aria-label="Workspace view"
      >
        <ToggleGroupItem value="conversation">
          <MessageSquare size={16} /> Conversation
        </ToggleGroupItem>
        <ToggleGroupItem value="results">
          <Package size={16} /> Results
          {visibleResult ? ` (${visibleResult.widget.products.length})` : ""}
        </ToggleGroupItem>
      </ToggleGroup>
      <section className="assistant-result-pane" aria-label="Product workspace">
        <div className="assistant-results-heading">
          <div>
            <div className="assistant-eyebrow">CUSTOMER PRODUCT WORKSPACE</div>
            <h2>
              {visibleResult
                ? visibleResult.widget.title ?? "Your product selection"
                : "A better fit starts with a brief"}
            </h2>
          </div>
          <span className="assistant-result-count">
            {visibleResult
              ? `${visibleResult.widget.products.length} products`
              : "Ready to explore"}
          </span>
        </div>
        {visibleResult && visibleResult.key !== latestResult?.key && (
          <div className="assistant-previous-result">
            Earlier results{" "}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOpenedResult(null)}
            >
              Show latest results
            </Button>
          </div>
        )}
        {visibleResult ? (
          <>
            {!visibleResult.widget.products.length && (
              <div className="assistant-workspace-empty">
                <Search size={32} />
                <h3>No matching products</h3>
                <p>
                  Keep the customer’s requirements or ask the assistant to
                  broaden the search. No constraints have been removed
                  automatically.
                </p>
              </div>
            )}
            <AssistantWidget
              key={visibleResult.key}
              data={visibleResult.widget}
              conversationId={id}
              selected={selected.map((product) => product.entityId)}
              disabled={blocked || !visibleResult.complete}
              onSelect={(product) =>
                setSelected((previous) =>
                  previous.some((item) => item.entityId === product.entityId)
                    ? previous.filter(
                        (item) => item.entityId !== product.entityId
                      )
                    : [...previous, product]
                )
              }
              onPrompt={(text) => void send(text)}
            />
            {resultWidgets.map((widget, index) => (
              <AssistantWidget
                key={`${visibleResult.key}:${index}`}
                data={widget}
                conversationId={id}
                selected={[]}
                disabled={blocked}
                onSelect={() => {}}
                onPrompt={(text) => void send(text)}
              />
            ))}
          </>
        ) : (
          <div className="assistant-workspace-empty">
            <Search size={32} />
            <h3>Find products for your customer</h3>
            <p>
              Tell the assistant about the market, occasion, and requirements.
              Product matches will stay here as you refine the brief.
            </p>
            <div className="assistant-lenses">
              <span>Commercial fit</span>
              <span>Consumer appeal</span>
              <span>Operator suitability</span>
              <span>Production & supply</span>
            </div>
          </div>
        )}
      </section>
      <div className="assistant-chat">
        <div className="assistant-panel-heading">
          <MessageSquare size={17} />
          <div>
            <h2>Your product assistant</h2>
            <p>Explore, refine, compare.</p>
          </div>
        </div>
        <div
          className="assistant-transcript"
          role="log"
          aria-label="Conversation messages"
          aria-busy={busy || serverBusy}
        >
          {loading && <p role="status">Loading conversation…</p>}
          {loadError && (
            <div role="alert">
              {loadError}
              <Button variant="outline" onClick={() => void reload()}>
                Reload
              </Button>
            </div>
          )}
          {!loading && !messages.length && !loadError && (
            <div className="assistant-welcome">
              <h2>Tell me about your customer</h2>
              <p>
                Ask a question, then select products from the proposals to
                refine or compare them.
              </p>
              {starters.map((starter) => (
                <Button
                  key={starter.title}
                  variant="outline"
                  disabled={blocked}
                  onClick={() => void send(starter.text)}
                >
                  {starter.title}
                  <ArrowUpRight size={14} />
                </Button>
              ))}
            </div>
          )}
          {messages.map((message) => (
            <article
              key={message.id}
              className={`assistant-message ${message.role}`}
            >
              <div className="assistant-message-author">
                {message.role === "user" ? "You" : "Article Assistant"}
                {message.metadata?.status === "incomplete" && (
                  <span> · Incomplete response</span>
                )}
              </div>
              {message.parts.map((part, index) =>
                part.type === "text" ? (
                  <div className="assistant-markdown" key={index}>
                    <ReactMarkdown>
                      {message.role === "assistant"
                        ? assistantText(
                            part.text,
                            message.metadata?.status !== "complete"
                          )
                        : part.text}
                    </ReactMarkdown>
                  </div>
                ) : part.type === "data-widget" &&
                  results.some(
                    (result) => result.key === `${message.id}:${index}`
                  ) ? (
                  <Button
                    key={index}
                    variant="outline"
                    className="assistant-open-results"
                    onClick={() => {
                      setOpenedResult({
                        key: `${message.id}:${index}`,
                        latest: latestResult?.key ?? "",
                      });
                      setMobileView("results");
                    }}
                  >
                    <Package size={15} />{" "}
                    {part.data.component === "ProductComparison"
                      ? "View comparison"
                      : "View product results"}
                    <ArrowUpRight size={15} />
                  </Button>
                ) : part.type === "data-widget" &&
                  (part.data?.component === "AttributeHighlights" ||
                    part.data?.component ===
                      "SourceReferences") ? null : part.type ===
                  "data-widget" ? (
                  <AssistantWidget
                    key={index}
                    data={part.data}
                    conversationId={id}
                    selected={selected.map((product) => product.entityId)}
                    disabled={
                      blocked || message.metadata?.status !== "complete"
                    }
                    onSelect={(product) =>
                      setSelected((previous) =>
                        previous.some(
                          (item) => item.entityId === product.entityId
                        )
                          ? previous.filter(
                              (item) => item.entityId !== product.entityId
                            )
                          : [...previous, product]
                      )
                    }
                    onPrompt={(text) => void send(text)}
                  />
                ) : part.type === "data-selection" ? (
                  <p className="assistant-muted" key={index}>
                    Selected products:{" "}
                    {part.data.map((product) => product.name).join(", ")}
                  </p>
                ) : null
              )}
              {message.role === "assistant" && (
                <button
                  className="assistant-copy"
                  aria-label="Copy response"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(
                        message.parts
                          .filter((part) => part.type === "text")
                          .map((part) => assistantText(part.text))
                          .join("\n")
                      );
                      setNotice("Response copied.");
                    } catch {
                      setNotice("Could not copy the response.");
                    }
                  }}
                >
                  <Copy size={13} /> Copy
                </button>
              )}
            </article>
          ))}
          {(busy || serverBusy) && (
            <p role="status">
              {serverBusy && !busy
                ? "A response is being generated. It will appear here when available…"
                : "Assistant is responding…"}
            </p>
          )}
          {error && <p role="alert">{error.message}</p>}
          {notice && <p role="status">{notice}</p>}
          {canRetry && (
            <Button variant="outline" onClick={() => void retry()}>
              <RotateCcw size={15} /> Retry latest message
            </Button>
          )}
          <div ref={end} />
        </div>
        <div className="assistant-composer">
          {selected.length > 0 && (
            <div className="assistant-selection">
              <span>
                {selected.length} selected:{" "}
                {selected.map((product) => product.name).join(", ")}
              </span>
              <div className="assistant-actions">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={
                    blocked || selected.length < 2 || selected.length > 3
                  }
                  onClick={() =>
                    void send(
                      "Look up the selected products again and compare their recorded commercial, consumer, operator, and production and supply facts."
                    )
                  }
                >
                  Compare selected (2–3)
                </Button>
                <AddToCollections
                  entityIds={selected.map((product) => product.entityId)}
                  name={`${selected.length} selected products`}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelected([])}
                >
                  Clear selection
                </Button>
              </div>
            </div>
          )}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void send(input);
            }}
          >
            <textarea
              aria-label="Message"
              maxLength={32000}
              value={input}
              disabled={blocked}
              onChange={(event) => setInput(event.target.value)}
              placeholder="Describe your customer’s needs…"
              rows={2}
              onKeyDown={(event) => {
                if (
                  event.key === "Enter" &&
                  !event.shiftKey &&
                  !event.nativeEvent.isComposing
                ) {
                  event.preventDefault();
                  void send(input);
                }
              }}
            />
            {busy ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => void stop()}
              >
                <Square size={15} /> Stop
              </Button>
            ) : (
              <Button type="submit" disabled={blocked || !input.trim()}>
                <Send size={15} /> Send
              </Button>
            )}
          </form>
          <p className="assistant-muted">
            Enter to send · Shift+Enter for a new line · Conversations are saved
            privately to your account.
          </p>
        </div>
      </div>
    </main>
  );
}
